import { Chip, Stack, Typography } from "@mui/material";
import React from "react";
import { ModalContext } from "../../../../Components/Modals";
import { GridRowsProp, GridColDef, GridRowParams, GridActionsCellItem } from "@mui/x-data-grid";
import DataTable, { CellSize } from "../../../../Components/Table/table";
import { orderService, userService } from "../../../../Core/Api/services";
import EditIcon from "@mui/icons-material/Edit";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import TableCellBool from "../../../../Components/Table/TableCellBool";
import ModalEditProduct, {
  ModalEditProductElementProps,
} from "../../../../Components/Modals/ModalEditProduct/modal-edit-product";
import { DbOrderProductInfos } from "../../../../Core/Api/Types/db-order-product-infos";
import { GroupAdvancedInfos } from "../../../../Core/Api/Types/group-advanced-infos";
import TableCellChip from "../../../../Components/Table/TableCellChip/table-cell-chip";
import { useTranslation } from "react-i18next";
import TableUuids from "../../../../Components/Table/table-uuids";
import { GetEnumValueLabelMap } from "../../../../Utils/helpers";
import { ObjectClass } from "../../../../Core/Api/Types/object-class";
import { ObjectLayer } from "../../../../Core/Api/Types/object-layer";
import { ObjectStatus } from "../../../../Core/Api/Types/object-status";

function AdminSettingsProductsManagement() {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = React.useState(true);
  const [rows, setRows] = React.useState<DbOrderProductInfos[]>([]);
  const [groups, setGroups] = React.useState<GroupAdvancedInfos[]>([]);

  // Table rows
  const apiGrid = useGridApiRef();

  // Modal
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };

  React.useEffect(() => {
    Promise.all([orderService.GetAllDeliveryProductsList(), userService.GetGroupsList()]).then(
      ([productsResonse, groupsResonse]) => {
        setRows(productsResonse.products || []);
        setGroups(groupsResonse.groups || []);
        setIsLoading(false);
      }
    );
  }, []);

  // #region Grid definition
  let _rows: GridRowsProp = [];
  type Row = (typeof _rows)[number];

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: "Id",
      flex: CellSize.xsmall,
      minWidth: CellSize.xsmall,
      renderCell: (data: Row) => (
        <Typography title={data.value} variant="body1">
          {data.value}
        </Typography>
      ),
    },
    {
      field: "name",
      headerName: t("common:name"),
      flex: CellSize.large,
      minWidth: CellSize.large,
      renderCell: (data: Row) => (
        <Typography title={data.value} variant="body1">
          {data.value || "-"}
        </Typography>
      ),
    },
    {
      headerName: t("common:enabled"),
      field: "enabled",
      type: "boolean",
      flex: CellSize.xsmall,
      minWidth: CellSize.xsmall,
      renderCell: (data: Row) => <TableCellBool value={data.value} />,
    },
    {
      field: "type",
      headerName: t("common:type"),
      flex: CellSize.large,
      minWidth: CellSize.large,
      renderCell: (data: Row) => (
        <Typography title={data.value} variant="body1" sx={{ wordWrap: "normal", wordBreak: "break-all" }}>
          {data.value || "-"}
        </Typography>
      ),
    },
    {
      field: "objectClasses",
      headerName: t("field:label.objectClassesLowerCase"),
      flex: CellSize.medium,
      minWidth: CellSize.medium,
      type: "singleSelect",
      valueOptions: GetEnumValueLabelMap(ObjectClass),
      renderCell: (data: Row) => <TableCellChip data={data} />,
    },
    {
      field: "layers",
      headerName: t("table:headerName.objectLayers"),
      flex: CellSize.medium,
      minWidth: CellSize.medium,
      type: "singleSelect",
      valueOptions: GetEnumValueLabelMap(ObjectLayer),
      renderCell: (data: Row) => <TableCellChip data={data} />,
    },
    {
      field: "assetStatuses",
      headerName: t("table:headerName.objectStatuses"),
      flex: CellSize.xlarge,
      minWidth: CellSize.xlarge,
      type: "singleSelect",
      valueOptions: GetEnumValueLabelMap(ObjectStatus),
      renderCell: (data: Row) => <TableCellChip data={data} />,
    },
    {
      field: "blockGroupIds",
      headerName: t("field:label.blockedGroups"),
      flex: CellSize.xlarge,
      minWidth: CellSize.xlarge,
      valueGetter: (data: Row, row: any) =>
        row?.blockGroupIds?.map((x) => groups.find((y) => y.id === x)?.group?.name || "") || [],
      renderCell: (data: Row) =>
        data?.row?.blockGroupIds && (
          <Stack sx={{ flexWrap: "wrap", gap: 0.5, my: 1 }} direction={"row"}>
            {data?.row?.blockGroupIds.map((x: number) => (
              <Chip label={groups.find((g) => g.id === x)?.group?.name || "-"} key={x} color="info" size="small" />
            ))}
          </Stack>
        ),
    },
    {
      headerName: "",
      field: "actions",
      type: "actions",
      align: "right",
      getActions: (params: GridRowParams) => [
        <GridActionsCellItem
          icon={<EditIcon sx={{ width: 20, height: 20, color: "primary.text" }} />}
          label={t("common:editUpperCase")}
          onClick={() => {
            handleEdit(params.row);
          }}
        />,
      ],
    },
  ];
  // #endregion

  const handleEdit = (prod) => {
    if (!openModal) return;
    openModal<ModalEditProductElementProps>(ModalEditProduct, {
      product: prod,
      groups: groups,
    });
  };

  const Table = React.useMemo(
    () => (
      <DataTable
        loading={isLoading}
        tableKey={TableUuids.SETTINGS_PRODUCTS}
        columns={columns}
        rows={rows}
        rowHeight={64}
        pagination={true}
        disableRowSelectionOnClick
        apiRef={apiGrid}
        getRowHeight={() => "auto"}
        onRowClick={(params) => handleEdit(rows.find((x) => x.id === params.id))}
      />
    ),
    [rows, isLoading, groups]
  );

  return <Stack sx={{ flex: "1 1 0", minHeight: 0 }}>{Table}</Stack>;
}

export default AdminSettingsProductsManagement;
